"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFeedback = exports.useDraftForMe = exports.useDescriptionRepository = exports.MAX_DESCRIPTION_LENGTH = void 0;
const tslib_1 = require("tslib");
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_messages_1 = require("@tableau/catalog-messages");
const SparklesIcon_1 = require("@tableau/icons/SparklesIcon");
const tableau_generative_ai_feedback_widget_1 = require("@tableau/tableau-generative-ai-feedback-widget");
const vizportalclient_common_components_1 = require("@tableau/vizportalclient-common-components");
const widgets_1 = require("@tableau/widgets");
const react_query_1 = require("@tanstack/react-query");
const formik_1 = require("formik");
const react_1 = tslib_1.__importDefault(require("react"));
exports.MAX_DESCRIPTION_LENGTH = 4000;
const restApiVersion = '3.23';
const queryEndpoints = Object.freeze({
    workbook: 'workbooks',
    database: 'databases',
    datasource: 'datasources',
    table: 'tables',
    flow: 'flows',
});
function getDescription({ contentType, contentId, makeJsonRestApiRequest }) {
    var _a, _b;
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const config = {
            endpoint: `${queryEndpoints[contentType]}/${contentId}`,
            httpMethod: 'GET',
            restApiVersion,
        };
        const result = yield makeJsonRestApiRequest(config);
        return (_b = (_a = result[contentType]) === null || _a === void 0 ? void 0 : _a.description) !== null && _b !== void 0 ? _b : '';
    });
}
function setDescription({ contentType, contentId, description, makeJsonRestApiRequest }) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const config = {
            endpoint: `${queryEndpoints[contentType]}/${contentId}`,
            httpMethod: 'PUT',
            restApiVersion,
        };
        yield makeJsonRestApiRequest(config, Object.fromEntries([[contentType, { description: description !== null && description !== void 0 ? description : null }]]));
        yield (0, vizportalclient_common_components_1.delayPromise)(2000);
    });
}
const suggestionConfig = {
    endpoint: 'metadataSuggestions/?suggestionType=description',
    httpMethod: 'POST',
    restApiVersion,
};
function getDescriptionSuggestion({ contentType, contentId, makeJsonRestApiRequest }) {
    var _a, _b;
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const payload = { contentList: { content: [{ contentType, id: contentId }] } };
        const suggestions = yield makeJsonRestApiRequest(suggestionConfig, payload);
        const suggestion = (_b = (_a = suggestions === null || suggestions === void 0 ? void 0 : suggestions.suggestionList) === null || _a === void 0 ? void 0 : _a.suggestion) === null || _b === void 0 ? void 0 : _b.find(t => t.contentId === contentId);
        return suggestion;
    });
}
const feedbackConfig = {
    endpoint: '/metadataSuggestions/feedback',
    httpMethod: 'POST',
    restApiVersion: 'exp',
};
function submitSuggestionFeedback(_a) {
    var { makeJsonRestApiRequest, suggestionId, feedback, feedbackText } = _a, rest = tslib_1.__rest(_a, ["makeJsonRestApiRequest", "suggestionId", "feedback", "feedbackText"]);
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        if (feedback === null) {
            return Promise.resolve();
        }
        const suggestionFeedback = feedback === 'BAD'
            ? Object.assign(Object.assign({}, rest), { suggestionId, feedback, feedbackText }) : { suggestionId, feedback, feedbackText };
        return yield makeJsonRestApiRequest(feedbackConfig, { suggestionFeedback });
    });
}
function useDescriptionRepository({ contentType, contentId, hideDialog, skipQuery, description }) {
    const queryClient = (0, react_query_1.useQueryClient)();
    const getDescriptionFn = (0, catalog_core_1.useRestFn)(getDescription);
    const setDescriptionFn = (0, catalog_core_1.useRestFn)(setDescription);
    const query = (0, react_query_1.useQuery)({
        queryKey: ['description', contentType, contentId],
        queryFn: () => getDescriptionFn({ contentType, contentId }),
        enabled: !skipQuery,
        initialData: description !== null && description !== void 0 ? description : undefined,
        refetchOnWindowFocus: false,
    });
    const mutation = (0, react_query_1.useMutation)({
        mutationFn: setDescriptionFn,
        onMutate: ({ contentId, contentType, description: newDescription }) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            const queryKey = ['description', contentType, contentId];
            yield queryClient.cancelQueries({ queryKey });
            const previousDescription = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, previousDescription);
            return { previousDescription, newDescription };
        }),
        onSettled: (_, __, { contentType, contentId }) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            const queryKey = ['description', contentType, contentId];
            yield queryClient.invalidateQueries({ queryKey });
            if (hideDialog) {
                hideDialog();
            }
        }),
        onError: (_, { contentType, contentId }, context) => {
            const key = ['description', contentType, contentId];
            widgets_1.ToastWidget.show({
                message: catalog_messages_1.Messages.EditDescription_Failure_Generic(),
                toastType: widgets_1.ToastType.Error,
                testId: 'edit-description-failed',
            });
            return queryClient.setQueryData(key, context === null || context === void 0 ? void 0 : context.previousDescription);
        },
        onSuccess: () => {
            widgets_1.ToastWidget.show({
                message: contentType === 'database' || contentType === 'table'
                    ? catalog_messages_1.Messages.editDescriptionSuccessfulWithWait()
                    : catalog_messages_1.Messages.editDescriptionSuccessful(),
                toastType: widgets_1.ToastType.Success,
                testId: 'edit-description-success',
            });
        },
    });
    return { query, mutation };
}
exports.useDescriptionRepository = useDescriptionRepository;
function useDraftForMe({ contentId, contentType, validate }) {
    const { setFieldValue, setFieldError, setFieldTouched } = (0, formik_1.useFormikContext)();
    const getDescriptionSuggestionFn = (0, catalog_core_1.useRestFn)(getDescriptionSuggestion);
    const queryFn = react_1.default.useCallback(() => tslib_1.__awaiter(this, void 0, void 0, function* () {
        try {
            const suggestion = yield getDescriptionSuggestionFn({ contentId, contentType });
            if (suggestion) {
                setFieldValue('description', suggestion.text);
                setFieldValue('suggestion', suggestion.suggestionId);
                setFieldTouched('description', true);
                setFieldError('description', validate(suggestion.text) === false ? 'invalid' : undefined);
            }
            return suggestion;
        }
        catch (err) {
            widgets_1.ToastWidget.show({
                message: isErrorCode(err, '404055')
                    ? catalog_messages_1.Messages.EditDescription_Failure_Backfill()
                    : catalog_messages_1.Messages.EditDescription_Failure_Generic(),
                testId: 'einstein-failure',
                toastType: widgets_1.ToastType.Error,
            });
            throw err;
        }
    }), [contentId, contentType, getDescriptionSuggestionFn, setFieldError, setFieldTouched, setFieldValue, validate]);
    const { isFetching, isError, refetch } = (0, react_query_1.useQuery)({
        queryKey: ['metadataSuggestions', contentType, contentId],
        queryFn,
        enabled: false,
        cacheTime: 0,
        retry: false,
    });
    const title = catalog_messages_1.Messages.EditDescription_DraftForMe();
    const button = (react_1.default.createElement(widgets_1.ButtonWidget, { buttonType: widgets_1.ButtonType.Primary, onClick: () => void refetch(), showSpinner: isFetching && !isError, disabled: isFetching && !isError, density: 'high', style: { display: 'inline-flex', alignItems: 'center' }, title: title, testId: 'draft' },
        react_1.default.createElement(SparklesIcon_1.SparklesIcon, { size: 14, style: { marginRight: 3 } }),
        title));
    return { button, isFetching };
}
exports.useDraftForMe = useDraftForMe;
function useFeedback() {
    const submitFeedbackFn = (0, catalog_core_1.useRestFn)(submitSuggestionFeedback);
    const { values, setStatus, initialStatus } = (0, formik_1.useFormikContext)();
    const submitFeedback = react_1.default.useCallback((f) => {
        setStatus('feedback');
        const suggestionId = values.suggestion;
        const func = () => tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                if (suggestionId) {
                    yield submitFeedbackFn(Object.assign(Object.assign({}, f), { suggestionId }));
                }
            }
            finally {
                setStatus(initialStatus);
            }
        });
        void func();
    }, [initialStatus, setStatus, submitFeedbackFn, values.suggestion]);
    if (values.suggestion) {
        return react_1.default.createElement(tableau_generative_ai_feedback_widget_1.TableauGenerativeAIFeedbackWidget, { onSubmitUserFeedback: submitFeedback });
    }
    return react_1.default.createElement(react_1.default.Fragment, null);
}
exports.useFeedback = useFeedback;
function isErrorCode(err, code) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const data = err && typeof err === 'object' && 'data' in err ? err.data : undefined;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const error = data && typeof data === 'object' && 'error' in data ? data.error : undefined;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const actual = error && typeof error === 'object' && 'code' in error ? error.code : undefined;
    return !!actual && typeof actual === 'string' && code === actual;
}
//# sourceMappingURL=DescriptionRepository.js.map